import { businessToday } from '../../utils/businessTime';
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  FiArrowLeft,
  FiEdit3,
  FiPrinter,
  FiMail,
  FiDollarSign,
  FiCreditCard,
  FiCalendar,
  FiUser,
  FiPhone,
  FiMapPin,
  FiPackage,
  FiDownload,
  FiAlertTriangle,
  FiCheck,
  FiX,
  FiPlus,
  FiSlash,
  FiFileText
} from 'react-icons/fi';
import { LoadingGrid } from '../../components/UI/Loading';
import { Modal } from '../../components/UI/Modal';
import { useFacturas } from '../../hooks/useFacturas';
import { facturasService, clientesService } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { estadoFacturaLabel, estadoFacturaBadgeClass, metodoPagoLabel } from '../../utils/facturaLabels';

const todayLocal = businessToday;

const FacturaDetalle = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  // Anular es una acción restringida (ventas.delete)
  const { hasPermission } = useAuth();
  const puedeAnular = hasPermission('ventas.delete');
  const { getFacturaById, markAsPaid, printFactura, downloadFactura, anularFactura, isLoading, error } = useFacturas();
  const [factura, setFactura] = useState(null);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [emailData, setEmailData] = useState({ destinatario: '', copias: '', asunto: '', mensaje: '' });
  const [tipoPagoCliente, setTipoPagoCliente] = useState(null);
  const [diasPagoCliente, setDiasPagoCliente] = useState(null);
  const [initialLoading, setInitialLoading] = useState(true);
  const [showAnularModal, setShowAnularModal] = useState(false);
  const [motivoAnulacion, setMotivoAnulacion] = useState('');
  const [isAnulando, setIsAnulando] = useState(false);
  const [anularError, setAnularError] = useState(null);
  const [pagoSeleccionado, setPagoSeleccionado] = useState(null);
  const [accionPago, setAccionPago] = useState(null); // 'editar' | 'revertir'
  const [pagoForm, setPagoForm] = useState({ amount: '', payment_date: '', reference_number: '', notes: '' });
  const [motivoReversa, setMotivoReversa] = useState('');
  const [pagoError, setPagoError] = useState(null);
  const [procesandoPago, setProcesandoPago] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentData, setPaymentData] = useState({
    amount: 0,
    method: 'efectivo',
    reference: '',
    notes: '',
    date: todayLocal()
  });

  useEffect(() => {
    loadFacturaDetalle();
  }, [id]);

  const loadFacturaDetalle = async () => {
    const result = await getFacturaById(id);
    if (result.success) {
      setFactura(result.data);
      if (result.data.clienteId) {
        const clienteResult = await clientesService.getCliente(result.data.clienteId);
        setTipoPagoCliente(clienteResult.success ? clienteResult.data.payment_type : null);
        setDiasPagoCliente(clienteResult.success ? clienteResult.data.payment_terms : null);
      }
    }
    setInitialLoading(false);
  };

  const handlePrintInvoice = async () => {
    const result = await printFactura(id);
    if (!result.success) {
      alert(result.message || 'Error al generar el PDF. Por favor intente nuevamente.');
    }
  };

  const handlePrintDialog = async () => {
    // Imprimir el PDF real de la factura, no la página del ERP.
    // El navegador muestra las impresoras instaladas o disponibles.
    const frame = document.createElement('iframe');
    frame.style.position = 'fixed';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    frame.setAttribute('aria-hidden', 'true');
    document.body.appendChild(frame);

    try {
      const result = await facturasService.generatePDF(id);
      if (!result.success) {
        throw new Error(result.message || 'No se pudo generar el PDF.');
      }
      const url = URL.createObjectURL(new Blob([result.data], { type: 'application/pdf' }));
      frame.onload = () => {
        try {
          frame.contentWindow.focus();
          frame.contentWindow.print();
        } catch (error) {
          console.error('No se pudo abrir el cuadro de impresión:', error);
          window.open(url, '_blank');
        }
        // Conservar el documento mientras el diálogo de impresión está abierto.
        setTimeout(() => {
          frame.remove();
          URL.revokeObjectURL(url);
        }, 120000);
      };
      frame.src = url;
    } catch (error) {
      frame.remove();
      alert(error.message || 'Error al preparar la impresión.');
    }
  };

  const handleDownloadInvoice = async () => {
    const result = await downloadFactura(id, factura.numeroFactura);
    if (!result.success) {
      alert(result.message || 'Error al descargar el PDF. Por favor intente nuevamente.');
    }
  };

  const handleEmailFactura = () => {
    setEmailData({
      destinatario: '',
      copias: '',
      asunto: `Factura ${factura.numeroFactura} - Betel Natural`,
      mensaje: ''
    });
    setShowEmailModal(true);
  };

  const processSendEmail = async () => {
    setIsSendingEmail(true);
    try {
      const payload = {
        destinatario: emailData.destinatario.trim() || null,
        copias: emailData.copias.trim() || null,
        asunto: emailData.asunto.trim() || null,
        mensaje: emailData.mensaje.trim() || null
      };
      const result = await facturasService.sendInvoiceEmail(id, payload);
      if (result.success) {
        alert(result.data?.message || `Factura ${factura.numeroFactura} enviada por correo`);
        setShowEmailModal(false);
      } else {
        alert(result.message || 'No se pudo enviar el correo. Verifique que el cliente tenga email registrado.');
      }
    } catch (error) {
      console.error('Error enviando factura por correo:', error);
      alert('Error al enviar el correo. Por favor intente nuevamente.');
    } finally {
      setIsSendingEmail(false);
    }
  };

  const handleEditFactura = () => {
    navigate(`/system/facturacion/${id}/editar`);
  };

  const abrirEdicionPago = (pago) => {
    setPagoSeleccionado(pago);
    setPagoForm({
      amount: pago.amount ?? '',
      payment_date: pago.payment_date ? String(pago.payment_date).split('T')[0] : '',
      reference_number: pago.reference_number || '',
      notes: pago.notes || ''
    });
    setPagoError(null);
    setAccionPago('editar');
  };

  const abrirReversaPago = (pago) => {
    setPagoSeleccionado(pago);
    setMotivoReversa('');
    setPagoError(null);
    setAccionPago('revertir');
  };

  const cerrarModalPago = () => {
    setAccionPago(null);
    setPagoSeleccionado(null);
    setPagoError(null);
    setMotivoReversa('');
  };

  const guardarPago = async () => {
    const monto = parseFloat(pagoForm.amount);
    if (!monto || monto <= 0) {
      setPagoError('El monto debe ser mayor a cero.');
      return;
    }

    setProcesandoPago(true);
    setPagoError(null);
    const result = await facturasService.updatePayment(id, pagoSeleccionado.id, {
      amount: monto,
      payment_date: pagoForm.payment_date || undefined,
      reference_number: pagoForm.reference_number || null,
      notes: pagoForm.notes || null
    });
    setProcesandoPago(false);

    if (result.success) {
      cerrarModalPago();
      await loadFacturaDetalle();
    } else {
      setPagoError(result.message || 'No se pudo corregir el pago.');
    }
  };

  const revertirPago = async () => {
    const motivo = motivoReversa.trim();
    if (!motivo) {
      setPagoError('El motivo de la reversa es obligatorio.');
      return;
    }

    setProcesandoPago(true);
    setPagoError(null);
    const result = await facturasService.reversePayment(id, pagoSeleccionado.id, motivo);
    setProcesandoPago(false);

    if (result.success) {
      cerrarModalPago();
      await loadFacturaDetalle();
    } else {
      setPagoError(result.message || 'No se pudo revertir el pago.');
    }
  };

  const handleAnular = () => {
    setMotivoAnulacion('');
    setAnularError(null);
    setShowAnularModal(true);
  };

  const processAnular = async () => {
    const motivo = motivoAnulacion.trim();
    if (!motivo) {
      setAnularError('El motivo de la anulación es obligatorio.');
      return;
    }

    setIsAnulando(true);
    setAnularError(null);
    const result = await anularFactura(id, motivo);
    setIsAnulando(false);

    if (result.success) {
      setFactura(result.data);
      setShowAnularModal(false);
    } else {
      setAnularError(result.message || 'No se pudo anular la factura.');
    }
  };

  const handlePayment = () => {
    setPaymentData(prev => ({
      ...prev,
      amount: factura.saldoPendiente
    }));
    setShowPaymentModal(true);
  };

  const handlePartialPayment = () => {
    setPaymentData(prev => ({
      ...prev,
      amount: 0
    }));
    setShowPaymentModal(true);
  };

  const processPayment = async () => {
    const paymentInfo = {
      payment_amount: parseFloat(paymentData.amount),
      payment_method: paymentData.method,
      payment_date: paymentData.date,
      payment_reference: paymentData.reference,
      notes: paymentData.notes
    };

    const result = await markAsPaid(id, paymentInfo);

    if (result.success) {
      setFactura(result.data);
      setShowPaymentModal(false);
      setPaymentData({
        amount: 0,
        method: 'efectivo',
        reference: '',
        notes: '',
        date: todayLocal()
      });
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(/^\d{4}-\d{2}-\d{2}$/.test(dateString) ? `${dateString}T12:00:00` : dateString).toLocaleDateString('es-CO', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (isLoading || initialLoading) {
    return <LoadingGrid message="Cargando información de la factura..." />;
  }

  if (!factura) {
    return (
      <div className="error-container">
        <h2>Factura no encontrada</h2>
        <button onClick={() => navigate('/system/facturacion')} className="btn btn--primary">
          Volver a Facturación
        </button>
      </div>
    );
  }

  // Acciones que modifican la factura. Se calculan aquí para no repetir las
  // condiciones en el JSX y para saber si hace falta pintar el separador.
  const puedeRegistrarPago = !factura.anulada && factura.saldoPendiente > 0;
  const puedeAnularFactura = puedeAnular && !factura.anulada && factura.montoAbonado <= 0;
  const mostrarAccionesPrincipales = puedeRegistrarPago || puedeAnularFactura;

  return (
    <div className="factura-detalle">
      {/* Header */}
      <div className="page-header">
        <button className="btn btn--ghost header-back" onClick={() => navigate('/system/facturacion')}>
          <FiArrowLeft size={20} />
        </button>

        <h1 className="page-title page-title--centered">
          Factura {factura.numeroFactura}
        </h1>

        {/* Acciones agrupadas por peso: primero las de consulta (no cambian
            nada), luego las que modifican la factura. El separador marca esa
            frontera para que Anular no quede junto a Imprimir. */}
        <div className="header-actions">
          <div className="header-actions__group">
            <button className="btn btn--outline" onClick={handlePrintInvoice} disabled={isLoading}>
              <FiFileText size={16} />
              Ver PDF
            </button>
            <button className="btn btn--outline" onClick={handlePrintDialog} disabled={isLoading}>
              <FiPrinter size={16} />
              Imprimir
            </button>
            <button className="btn btn--outline" onClick={handleDownloadInvoice} disabled={isLoading}>
              <FiDownload size={16} />
              Descargar PDF
            </button>
            {!factura.anulada && (
              <button className="btn btn--outline" onClick={handleEmailFactura} disabled={isLoading}>
                <FiMail size={16} />
                Enviar por correo
              </button>
            )}
            {!factura.anulada && !(factura.montoAbonado > 0 && factura.saldoPendiente === 0) && (
              <button className="btn btn--outline" onClick={handleEditFactura} disabled={isLoading}>
                <FiEdit3 size={16} />
                Editar
              </button>
            )}
          </div>

          {mostrarAccionesPrincipales && (
            <>
              <span className="header-actions__sep" aria-hidden="true" />

              <div className="header-actions__group">
                {puedeRegistrarPago && (
                  <button className="btn btn--primary" onClick={handlePayment}>
                    <FiDollarSign size={16} />
                    Registrar Pago
                  </button>
                )}
                {puedeAnularFactura && (
                  <button className="btn btn--danger" onClick={handleAnular} disabled={isLoading}>
                    <FiSlash size={16} />
                    Anular
                  </button>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* El mismo envío de correo disponible en el listado general de facturación */}
      <Modal
        isOpen={showEmailModal}
        onClose={() => { if (!isSendingEmail) setShowEmailModal(false); }}
        title={`Enviar por Correo - ${factura.numeroFactura}`}
        size="medium"
      >
        <div className="modal-body">
          <div className="payment-info-summary">
            <p><strong>Cliente:</strong> {factura.clienteNombre}</p>
            <p><strong>Total de la factura:</strong> {formatCurrency(factura.total)}</p>
            {factura.saldoPendiente > 0 && <p><strong>Saldo pendiente:</strong> {formatCurrency(factura.saldoPendiente)}</p>}
            <p><strong>Adjunto:</strong> PDF de la factura (se genera automáticamente)</p>
          </div>
          <div className="form-group">
            <label className="form-label">Destinatario</label>
            <input type="email" className="form-input" value={emailData.destinatario}
              onChange={(e) => setEmailData(prev => ({ ...prev, destinatario: e.target.value }))}
              placeholder="Correo registrado del cliente (por defecto)" />
            <small className="form-helper">Déjalo vacío para enviar al correo registrado del cliente.</small>
          </div>
          <div className="form-group">
            <label className="form-label">Copias (CC)</label>
            <input type="text" className="form-input" value={emailData.copias}
              onChange={(e) => setEmailData(prev => ({ ...prev, copias: e.target.value }))}
              placeholder="correo1@dominio.com, correo2@dominio.com" />
          </div>
          <div className="form-group">
            <label className="form-label">Asunto</label>
            <input type="text" className="form-input" maxLength={200} value={emailData.asunto}
              onChange={(e) => setEmailData(prev => ({ ...prev, asunto: e.target.value }))} />
          </div>
          <div className="form-group">
            <label className="form-label">Mensaje</label>
            <textarea className="form-input" rows={4} maxLength={5000} value={emailData.mensaje}
              onChange={(e) => setEmailData(prev => ({ ...prev, mensaje: e.target.value }))}
              placeholder="Déjalo vacío para usar el mensaje estándar (número de factura, total, vencimiento y saldo)." />
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn--outline" onClick={() => setShowEmailModal(false)} disabled={isSendingEmail}>
            <FiX size={18} /><span>Cancelar</span>
          </button>
          <button className="btn btn--primary" onClick={processSendEmail} disabled={isSendingEmail}>
            <FiMail size={18} /><span>{isSendingEmail ? 'Enviando...' : 'Enviar Correo'}</span>
          </button>
        </div>
      </Modal>
      {/* Aviso de factura anulada */}
      {factura.anulada && (
        <div className="alert alert--warning" style={{ marginBottom: '20px' }}>
          <FiSlash className="alert-icon" />
          <div className="alert-content">
            <strong>Factura anulada{factura.fechaAnulacion ? ` el ${formatDate(factura.fechaAnulacion)}` : ''}</strong>
            {factura.motivoAnulacion && (
              <p style={{ marginTop: '6px' }}>Motivo: {factura.motivoAnulacion}</p>
            )}
            <p style={{ marginTop: '6px' }}>
              Esta factura no se cobra ni entra en los reportes. La orden que la generó
              volvió a quedar disponible para facturar.
            </p>
          </div>
        </div>
      )}

      {/* Card 1: Información Principal de la Factura */}
      <div className="invoice-header-card">
        <div className="invoice-header-card__left">
          <div className="invoice-icon-circle">
            <FiFileText size={32} />
          </div>
          <div className="invoice-header-info">
            <h3 className="invoice-header-title">Factura {factura.numeroFactura}</h3>
            <p className="invoice-header-subtitle">
              <span className={`status-badge ${estadoFacturaBadgeClass(factura.estado)}`}>
                {estadoFacturaLabel(factura.estado)}
              </span>
            </p>
          </div>
        </div>

        <div className="invoice-header-card__grid">
          {/* Cliente */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiUser size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Cliente</label>
              <div className="invoice-field-value">{factura.clienteNombre}</div>
            </div>
          </div>

          {/* Fecha de Emisión */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiCalendar size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Fecha de Emisión</label>
              <div className="invoice-field-value">{formatDate(factura.fecha)}</div>
            </div>
          </div>

          {/* Fecha de Vencimiento */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiCalendar size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Fecha de Vencimiento</label>
              <div className="invoice-field-value">{formatDate(factura.fechaVencimiento)}</div>
            </div>
          </div>

          {/* Método de Pago */}
          <div className="invoice-field">
            <div className="invoice-field-icon">
              <FiCreditCard size={18} />
            </div>
            <div className="invoice-field-content">
              <label className="invoice-field-label">Tipos de Pago</label>
              <div className="invoice-field-value">{tipoPagoCliente === 'COD' ? 'COD (Pago antes de enviar la orden)' : tipoPagoCliente === 'TERMINOS' ? `Términos: ${diasPagoCliente != null ? diasPagoCliente + ' días' : 'días no configurados'}` : metodoPagoLabel(factura.metodoPago)}</div>
            </div>
          </div>

          {/* Notas */}
          {factura.notasInternas && (
            <div className="invoice-field invoice-field--full">
              <div className="invoice-field-content">
                <label className="invoice-field-label">Notas Internas (solo equipo Betel)</label>
                <div className="invoice-field-value">{factura.notasInternas}</div>
              </div>
            </div>
          )}
          {factura.notas && (
            <div className="invoice-field invoice-field--full">
              <div className="invoice-field-icon">
                <FiFileText size={18} />
              </div>
              <div className="invoice-field-content">
                <label className="invoice-field-label">Notas de la Orden</label>
                <div className="invoice-field-value">{factura.notas}</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Card 2: Tabla de Factura */}
      {factura.items && factura.items.length > 0 ? (
        <div className="invoice-table-card">
          <div className="invoice-table-header">
            <div className="table-header-icon">
              <FiPackage size={24} />
            </div>
            <div className="table-header-content">
              <h3 className="table-header-title">Productos en la Factura</h3>
              <p className="table-header-subtitle">{factura.items.length} producto(s)</p>
            </div>
          </div>

          <div className="invoice-table-wrapper">
            <table className="invoice-table">
              <thead className="invoice-table-head">
                <tr>
                  <th className="th-product">Producto</th>
                  <th className="th-qty">Cantidad</th>
                  <th className="th-price">Precio Unit.</th>
                  <th className="th-discount">Desc.</th>
                  <th className="th-total">Total</th>
                </tr>
              </thead>
              <tbody className="invoice-table-body">
                {factura.items.map((item, index) => (
                  <tr key={index} className="invoice-table-row">
                    <td className="td-product">
                      <div className="product-cell">
                        <div className="product-cell-icon">
                          <FiPackage size={16} />
                        </div>
                        <div className="product-cell-info">
                          <div className="product-cell-name">{item.description || item.product_name}</div>
                          <div className="product-cell-code">{item.product_id}</div>
                        </div>
                      </div>
                    </td>
                    <td className="td-qty">
                      <span className="qty-badge">{parseFloat(item.quantity)}</span>
                    </td>
                    <td className="td-price">
                      <span className="price-value">{formatCurrency(item.unit_price)}</span>
                    </td>
                    <td className="td-discount">
                      {item.discount_percentage > 0 ? (
                        <span className="discount-badge">{item.discount_percentage}%</span>
                      ) : (
                        <span className="no-discount">—</span>
                      )}
                    </td>
                    <td className="td-total">
                      <span className="total-value">{formatCurrency(item.total)}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Totales */}
          <div className="invoice-totals-section">
            <div className="totals-container">
              <div className="totals-row">
                <span className="totals-label">Subtotal</span>
                <span className="totals-value">{formatCurrency(factura.subtotal)}</span>
              </div>
              {factura.descuento > 0 && (
                <div className="totals-row totals-row--discount">
                  <span className="totals-label">Descuento</span>
                  <span className="totals-value">-{formatCurrency(factura.descuento)}</span>
                </div>
              )}
              {factura.costoEnvio > 0 && (
                <div className="totals-row">
                  <span className="totals-label">Envío</span>
                  <span className="totals-value">{formatCurrency(factura.costoEnvio)}</span>
                </div>
              )}
              <div className="totals-divider"></div>
              <div className="totals-row totals-row--final">
                <span className="totals-label">Total</span>
                <span className="totals-value">{formatCurrency(factura.total)}</span>
              </div>
              {factura.montoAbonado > 0 && (
                <>
                  <div className="totals-row">
                    <span className="totals-label">Pagado</span>
                    <span className="totals-value">-{formatCurrency(factura.montoAbonado)}</span>
                  </div>
                  <div className="totals-row totals-row--balance">
                    <span className="totals-label">Saldo Pendiente</span>
                    <span className="totals-value">{formatCurrency(factura.saldoPendiente)}</span>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="invoice-empty-state">
          <div className="empty-state-icon">
            <FiPackage size={64} />
          </div>
          <h3 className="empty-state-title">No hay productos en esta factura</h3>
        </div>
      )}

      {/* Historial de pagos */}
      {factura.pagos && factura.pagos.length > 0 && (
        <div className="invoice-table-card">
          <div className="invoice-table-header">
            <div className="table-header-icon">
              <FiDollarSign size={24} />
            </div>
            <div className="table-header-content">
              <h3 className="table-header-title">Historial de Pagos</h3>
              <p className="table-header-subtitle">
                Total pagado: {formatCurrency(factura.montoAbonado)}
              </p>
            </div>
          </div>
          <div className="invoice-table-wrapper">
            <table className="invoice-table">
              <thead className="invoice-table-head">
                <tr>
                  <th>Fecha</th>
                  <th>Método</th>
                  <th>Referencia</th>
                  <th className="text-right">Monto</th>
                  {puedeAnular && !factura.anulada && <th className="text-center">Acciones</th>}
                </tr>
              </thead>
              <tbody className="invoice-table-body">
                {factura.pagos.map((pago, index) => (
                  <tr
                    key={pago.id || index}
                    className={`invoice-table-row ${pago.is_reversed ? 'invoice-table-row--reversed' : ''}`}
                  >
                    <td>{formatDate(pago.payment_date)}</td>
                    <td>{metodoPagoLabel(pago.payment_method)}</td>
                    <td>
                      {pago.reference_number || '-'}
                      {pago.is_reversed && (
                        <div className="payment-reversed-note">
                          Revertido{pago.reversal_reason ? `: ${pago.reversal_reason}` : ''}
                        </div>
                      )}
                    </td>
                    <td className="text-right">
                      <span className="total-value">{formatCurrency(pago.amount)}</span>
                    </td>
                    {puedeAnular && !factura.anulada && (
                      <td className="text-center">
                        {!pago.is_reversed && (
                          <>
                            <button
                              className="btn-edit-invoice"
                              onClick={() => abrirEdicionPago(pago)}
                              title="Corregir pago"
                            >
                              <FiEdit3 />
                            </button>
                            <button
                              className="btn-void-invoice"
                              onClick={() => abrirReversaPago(pago)}
                              title="Revertir pago"
                            >
                              <FiSlash />
                            </button>
                          </>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal de Pago */}
      <Modal
        isOpen={showPaymentModal}
        onClose={() => setShowPaymentModal(false)}
        title="Registrar Pago"
        size="medium"
      >
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Monto del Pago *</label>
            <input
              type="number"
              className="form-input"
              value={paymentData.amount}
              onChange={(e) => setPaymentData(prev => ({ ...prev, amount: e.target.value }))}
              placeholder="0.00"
              min="0"
              max={factura.saldoPendiente}
              step="0.01"
            />
            <small className="form-helper">
              Saldo pendiente: {formatCurrency(factura.saldoPendiente)}
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Método de Pago *</label>
            <select
              className="form-input"
              value={paymentData.method}
              onChange={(e) => setPaymentData(prev => ({ ...prev, method: e.target.value }))}
            >
              <option value="efectivo">Efectivo</option>
              <option value="transferencia">Transferencia</option>
              <option value="cheque">Cheque</option>
              <option value="tarjeta">Tarjeta</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Fecha del Pago</label>
            <input
              type="date"
              className="form-input"
              value={paymentData.date}
              onChange={(e) => setPaymentData(prev => ({ ...prev, date: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Referencia</label>
            <input
              type="text"
              className="form-input"
              value={paymentData.reference}
              onChange={(e) => setPaymentData(prev => ({ ...prev, reference: e.target.value }))}
              placeholder="Número de cheque, transferencia, etc."
            />
          </div>

          <div className="form-group">
            <label className="form-label">Notas</label>
            <textarea
              className="form-input"
              value={paymentData.notes}
              onChange={(e) => setPaymentData(prev => ({ ...prev, notes: e.target.value }))}
              placeholder="Notas adicionales sobre el pago"
              rows={3}
            />
          </div>
        </div>

        <div className="modal-footer">
          <button
            className="btn btn--outline"
            onClick={() => setShowPaymentModal(false)}
          >
            <FiX size={16} />
            Cancelar
          </button>
          <button
            className="btn btn--primary"
            onClick={processPayment}
            disabled={!paymentData.amount || paymentData.amount <= 0 || isLoading}
          >
            <FiCheck size={16} />
            Registrar Pago
          </button>
        </div>
      </Modal>

      {/* Modal de corrección de pago */}
      <Modal
        isOpen={accionPago === 'editar'}
        onClose={cerrarModalPago}
        title="Corregir Pago"
        size="medium"
      >
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Monto *</label>
            <input
              type="number"
              className="form-input"
              value={pagoForm.amount}
              onChange={(e) => setPagoForm(prev => ({ ...prev, amount: e.target.value }))}
              min="0"
              step="0.01"
            />
            <small className="form-helper">
              El saldo de la factura se recalcula con los pagos vigentes.
            </small>
          </div>

          <div className="form-group">
            <label className="form-label">Fecha del pago</label>
            <input
              type="date"
              className="form-input"
              value={pagoForm.payment_date}
              onChange={(e) => setPagoForm(prev => ({ ...prev, payment_date: e.target.value }))}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Referencia</label>
            <input
              type="text"
              className="form-input"
              value={pagoForm.reference_number}
              onChange={(e) => setPagoForm(prev => ({ ...prev, reference_number: e.target.value }))}
              maxLength={100}
            />
          </div>

          <div className="form-group">
            <label className="form-label">Notas</label>
            <textarea
              className="form-input"
              value={pagoForm.notes}
              onChange={(e) => setPagoForm(prev => ({ ...prev, notes: e.target.value }))}
              rows={3}
            />
          </div>

          {pagoError && (
            <div className="alert alert--danger">
              <FiAlertTriangle className="alert-icon" />
              <div className="alert-content">{pagoError}</div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn--outline" onClick={cerrarModalPago} disabled={procesandoPago}>
            <FiX size={16} />
            Cancelar
          </button>
          <button className="btn btn--primary" onClick={guardarPago} disabled={procesandoPago}>
            <FiCheck size={16} />
            {procesandoPago ? 'Guardando...' : 'Guardar Cambios'}
          </button>
        </div>
      </Modal>

      {/* Modal de reversa de pago */}
      <Modal
        isOpen={accionPago === 'revertir'}
        onClose={cerrarModalPago}
        title="Revertir Pago"
        size="medium"
      >
        <div className="modal-body">
          <div className="alert alert--warning" style={{ marginBottom: '16px' }}>
            <FiAlertTriangle className="alert-icon" />
            <div className="alert-content">
              <strong>El pago no se elimina</strong>
              <p style={{ marginTop: '6px' }}>
                Queda en el historial marcado como revertido, con el motivo y tu usuario,
                y deja de contar en el saldo de la factura y en el estado de cuenta.
              </p>
            </div>
          </div>

          {pagoSeleccionado && (
            <div className="payment-info-summary">
              <p><strong>Monto:</strong> {formatCurrency(pagoSeleccionado.amount)}</p>
              <p><strong>Fecha:</strong> {formatDate(pagoSeleccionado.payment_date)}</p>
              <p><strong>Método:</strong> {metodoPagoLabel(pagoSeleccionado.payment_method)}</p>
            </div>
          )}

          <div className="form-group">
            <label className="form-label">Motivo de la reversa *</label>
            <textarea
              className="form-input"
              value={motivoReversa}
              onChange={(e) => setMotivoReversa(e.target.value)}
              placeholder="Ej: El cobro correspondía a otra factura"
              rows={3}
              maxLength={255}
            />
          </div>

          {pagoError && (
            <div className="alert alert--danger">
              <FiAlertTriangle className="alert-icon" />
              <div className="alert-content">{pagoError}</div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn--outline" onClick={cerrarModalPago} disabled={procesandoPago}>
            <FiX size={16} />
            Cancelar
          </button>
          <button
            className="btn btn--danger"
            onClick={revertirPago}
            disabled={procesandoPago || !motivoReversa.trim()}
          >
            <FiSlash size={16} />
            {procesandoPago ? 'Revirtiendo...' : 'Revertir Pago'}
          </button>
        </div>
      </Modal>

      {/* Modal de anulación */}
      <Modal
        isOpen={showAnularModal}
        onClose={() => setShowAnularModal(false)}
        title={`Anular Factura ${factura.numeroFactura}`}
        size="medium"
      >
        <div className="modal-body">
          <div className="alert alert--warning" style={{ marginBottom: '16px' }}>
            <FiAlertTriangle className="alert-icon" />
            <div className="alert-content">
              <strong>La factura no se elimina</strong>
              <p style={{ marginTop: '6px' }}>
                Queda registrada como anulada con el motivo, la fecha y tu usuario.
                Se excluye de los reportes y del estado de cuenta, y la orden que la
                generó vuelve a quedar disponible para facturar.
              </p>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Motivo de la anulación *</label>
            <textarea
              className="form-input"
              value={motivoAnulacion}
              onChange={(e) => setMotivoAnulacion(e.target.value)}
              placeholder="Ej: Error de digitación en las cantidades"
              rows={3}
              maxLength={255}
            />
            <small className="form-helper">Máximo 255 caracteres.</small>
          </div>

          {anularError && (
            <div className="alert alert--danger">
              <FiAlertTriangle className="alert-icon" />
              <div className="alert-content">{anularError}</div>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <button
            className="btn btn--outline"
            onClick={() => setShowAnularModal(false)}
            disabled={isAnulando}
          >
            <FiX size={16} />
            Cancelar
          </button>
          <button
            className="btn btn--danger"
            onClick={processAnular}
            disabled={isAnulando || !motivoAnulacion.trim()}
          >
            <FiSlash size={16} />
            {isAnulando ? 'Anulando...' : 'Anular Factura'}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default FacturaDetalle;